import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button, Drawer, Row, Tag, Tooltip } from "@narmi/design_system";
import {
  useFetchPaginatedPaymentActivity,
  useFetchPaymentMetadataById,
  useFormattedPaginatedPaymentActivity,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { useFetchBulkPaymentById } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import Filters from "byzantine/src/filters";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import DualApprovalRecurringDialog, {
  RECURRING_DIALOG_STORAGE_KEY,
} from "../DualApprovalRecurringDialog";
import BulkApprovalDialog, {
  BULK_APPROVAL_DIALOG_STORAGE_KEY,
} from "../BulkApprovalDialog";
import styles from "../DualApproval.module.scss";
import shouldShowDueSoon from "./utils";
import {
  TopCard,
  BulkPaymentTopCard,
  WireImportTopCard,
  BulkReversalTopCard,
} from "./TopCards";
import RecipientDetailsCard from "./RecipientDetailsCard";
import PaymentDetailsCard from "./PaymentDetailsCard";
import { ReviewPaymentDialog } from "../../PaymentActivity/CreateBulk";
import WireReviewPaymentDialog from "../../PaymentActivity/WireFileImport/WireReviewPaymentDialog";
import { PAYMENT_ACTIVITY_ROUTE } from "../../PaymentActivity/routes";

type DualApprovalDrawerProps = {
  isOpen: boolean;
  showControls: boolean;
  handleClose: () => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  onFetchError?: () => void;
  handleApprove?: () => void;
  handleReject?: () => void;
  isActionLoading?: boolean;
  dualApproval: PaginatedDualApproval | null;
  drawerType?: "pending" | "history";
  isReadOnly?: boolean;
  titleL10nKey?: string;
};

const DualApprovalDrawer = ({
  isOpen,
  showControls,
  handleClose,
  handleOnNext,
  handleOnPrev,
  onFetchError,
  handleApprove,
  handleReject,
  isActionLoading = false,
  dualApproval,
  drawerType = "pending",
  isReadOnly = false,
  titleL10nKey = "pending-drawer-title",
}: DualApprovalDrawerProps) => {
  const DIALOG_TABLE_SIZE = 5;
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const { send: fetchPage } = useFetchPaginatedPaymentActivity();
  const { send: fetchPaymentMetadata } = useFetchPaymentMetadataById();
  const { send: fetchBulkPaymentById } = useFetchBulkPaymentById();
  const [originalPayment, setOriginalPayment] = useState<API.ACHPayment | null>(
    null
  );
  const [parentBulkPayment, setParentBulkPayment] =
    useState<API.BulkPayment | null>(null);
  const [visibleDialog, setVisibleDialog] = useState<
    "recurring" | "bulkApproval" | ""
  >("");
  const [isBulkPaymentsDialogOpen, setIsBulkPaymentsDialogOpen] =
    useState(false);
  const [isWireImportDialogOpen, setIsWireImportDialogOpen] = useState(false);
  const [wireImportBatch, setWireImportBatch] =
    useState<API.WireImport.WireImportBatch | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const paymentsForDialog = useFormattedPaginatedPaymentActivity(pageNumber);
  const wireImportPaymentsForDialog = useMemo<
    API.WireImport.CSVParsedRow[]
  >(() => {
    if (!wireImportBatch?.wires?.length) {
      return [];
    }

    return wireImportBatch.wires.map((wire, index) => {
      const memoParts = [wire.memo_1, wire.memo_2, wire.memo_3, wire.memo_4]
        .map((part) => part?.trim())
        .filter(Boolean);
      const recipient = {
        id: wire.id as unknown as API.RecipientId,
        name: wire.beneficiary_name || "",
        fedwire_destination: {
          institution_name: wire.to_account_institution_name || "",
          account_number: wire.to_account_number || "",
          routing_number: wire.to_account_routing_number || "",
        },
      } as API.Recipient;

      return {
        row_index: index,
        recipient,
        amount: -wire.amount / 100,
        memo: memoParts.join(" ") || null,
        wire_reason: wire.wire_reason || null,
        recipient_name: wire.beneficiary_name || null,
        routing_number: wire.to_account_routing_number || null,
        account_number: wire.to_account_number || null,
        recipient_street_address: wire.beneficiary_address_1 || null,
        recipient_city: null,
        recipient_region_code: null,
        recipient_postal_code: null,
        field_errors: null,
        needs_review: false,
        warning: "",
        row_errors: [],
      };
    });
  }, [wireImportBatch?.wires]);

  useEffect(() => {
    if (drawerContentRef.current) {
      drawerContentRef.current.scrollIntoView({
        behavior: "instant",
      });
    }
  }, [isOpen, dualApproval?.id]);

  useEffect(() => {
    const isReversal = Boolean(dualApproval?.achReversalId);
    if (!isReversal || !dualApproval?.achId) {
      setOriginalPayment(null);
      return undefined;
    }
    setOriginalPayment(null);
    let cancelled = false;
    fetchPaymentMetadata(dualApproval.achId, "UnverifiedACHTransaction").then(
      (data) => {
        if (!cancelled && data) {
          setOriginalPayment(data as API.ACHPayment);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [dualApproval?.achReversalId, dualApproval?.achId, fetchPaymentMetadata]);

  // for bulk reversals, fetch the parent bulk payment (aka the original bulk payment) so we can render the
  // same payment details we show for a bulk payment dual approval
  useEffect(() => {
    const parentId = dualApproval?.bulkReversalParentPaymentId;
    if (!isOpen || !dualApproval?.bulkReversalId || !parentId) {
      setParentBulkPayment(null);
      return undefined;
    }
    setParentBulkPayment(null);
    let cancelled = false;
    fetchBulkPaymentById(parentId)
      .then((data) => {
        if (!cancelled && data) {
          setParentBulkPayment(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setParentBulkPayment(null);
          onFetchError?.();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    dualApproval?.bulkReversalId,
    dualApproval?.bulkReversalParentPaymentId,
    fetchBulkPaymentById,
    onFetchError,
  ]);

  useEffect(() => {
    let bulkFilter: string | null = null;
    if (dualApproval?.bulkPaymentId) {
      bulkFilter = `bulk_payment=${dualApproval.bulkPaymentId}`;
    } else if (dualApproval?.bulkReversalId) {
      bulkFilter = `bulk_reversal=${dualApproval.bulkReversalId}`;
    }

    if (isOpen && bulkFilter) {
      const offsetIndex = (pageNumber - 1) * DIALOG_TABLE_SIZE;
      fetchPage(
        pageNumber,
        offsetIndex,
        DIALOG_TABLE_SIZE,
        bulkFilter,
        "&exclude_completed_one_time=true&exclude_approval_expired_one_time=true"
      );
    }
  }, [
    isOpen,
    dualApproval?.bulkPaymentId,
    dualApproval?.bulkReversalId,
    pageNumber,
    fetchPage,
    DIALOG_TABLE_SIZE,
  ]);

  useEffect(() => {
    let abortController: AbortController | null = null;

    if (!isOpen || !dualApproval?.wireImportBatchId || !dualApproval?.id) {
      setWireImportBatch(null);
    } else {
      const approvalRequestUrl = `/v1/approval_requests/${dualApproval.id}?include_wires=true`;
      abortController = new AbortController();

      fetch(approvalRequestUrl, {
        credentials: "include",
        signal: abortController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error();
          }
          return response.json();
        })
        .then((data) => {
          setWireImportBatch(data.approval_request?.wire_import_batch ?? null);
        })
        .catch((error) => {
          if (error?.name === "AbortError") {
            return;
          }
          setWireImportBatch(null);
          onFetchError?.();
        });
    }

    return () => {
      abortController?.abort();
    };
  }, [isOpen, dualApproval?.wireImportBatchId, dualApproval?.id, onFetchError]);

  useEffect(() => {
    setPageNumber(1);
    setIsBulkPaymentsDialogOpen(false);
    setIsWireImportDialogOpen(false);
    setWireImportBatch(null);
    setVisibleDialog("");
  }, [dualApproval?.id]);

  if (!dualApproval || !isOpen) return null;

  const isPendingDrawer = drawerType === "pending";
  const isHistoryDrawer = drawerType === "history";
  const showActions = Boolean(
    isPendingDrawer &&
      !isReadOnly &&
      !dualApproval.isOwnRequest &&
      handleApprove &&
      handleReject
  );
  const isBulk = Boolean(dualApproval.bulkPaymentId);
  const isBulkReversal = Boolean(dualApproval.bulkReversalId);
  const isWire = Boolean(dualApproval.wireId);
  const isInternationalWire = Boolean(dualApproval.internationalWireId);
  const isACH = Boolean(dualApproval.achId);
  const isReversal = Boolean(dualApproval.achReversalId);
  const isScheduledACH = Boolean(dualApproval.scheduledACHId);
  const isWireImportBatch = Boolean(dualApproval.wireImportBatchId);
  const isDueSoon =
    isPendingDrawer && !isReadOnly && shouldShowDueSoon(dualApproval);
  const isOneTimeScheduledACH =
    isScheduledACH && dualApproval.schedule?.recurrence === 1;
  const doNotShowRecurringDialog = Boolean(
    window.localStorage.getItem(RECURRING_DIALOG_STORAGE_KEY)
  );
  const doNotShowBulkApprovalDialog = Boolean(
    window.localStorage.getItem(BULK_APPROVAL_DIALOG_STORAGE_KEY)
  );

  const toggleBulkPaymentsDialog = () => {
    setIsBulkPaymentsDialogOpen((prev) => !prev);
  };

  const toggleWireImportDialog = () => {
    setIsWireImportDialogOpen((prev) => !prev);
  };

  const handleClickOriginalPayment = () => {
    if (dualApproval.bulkReversalParentPaymentId) {
      navigate(
        `/${PAYMENT_ACTIVITY_ROUTE}/bulk/${dualApproval.bulkReversalParentPaymentId}`
      );
    } else if (dualApproval.achId) {
      navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${dualApproval.achId}`);
    }
  };

  const toggleRecurringDialog = () => {
    setVisibleDialog((prev) => (prev === "recurring" ? "" : "recurring"));
  };

  const toggleBulkApprovalDialog = () => {
    setVisibleDialog((prev) => (prev === "bulkApproval" ? "" : "bulkApproval"));
  };

  const handleClickApprove = () => {
    if (isScheduledACH && !isOneTimeScheduledACH && !doNotShowRecurringDialog) {
      toggleRecurringDialog();
      return;
    }

    if (isBulk && !doNotShowBulkApprovalDialog) {
      toggleBulkApprovalDialog();
      return;
    }

    handleApprove?.();
  };

  const getDeliveryCopy = () => {
    if (!isPendingDrawer) return null;
    if (isWire) return l10n.getString("wire-delivery-copy");
    if (isInternationalWire)
      return l10n.getString("international-wire-delivery-copy");
    if (isBulk && dualApproval.bulk?.isExpedited) return null;
    if (isReversal || isACH || isScheduledACH || isBulk || isBulkReversal)
      return l10n.getString("ach-delivery-copy");
    return null;
  };
  const deliveryCopy = getDeliveryCopy();

  const getStatusTagKind = (status: string) => {
    if (status === "rejected" || status === "expired") {
      return "error";
    }
    return "success";
  };

  const renderStatusIndicator = () => {
    if (isHistoryDrawer) {
      return (
        <Tag
          kind={getStatusTagKind(dualApproval.status)}
          label={Filters.humanize(dualApproval.status)}
        />
      );
    }

    if (isDueSoon) {
      return (
        <Tooltip maxWidth="250px" text={l10n.getString("due-soon-tooltip")}>
          <Button kind="plain" testId="due-soon-tooltip">
            <Tag
              kind="error"
              label={l10n.getString("approval-drawer-due-soon-tag")}
            />
          </Button>
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <>
      <Drawer
        depth="524px"
        isOpen={isOpen}
        showControls={showControls}
        onUserDismiss={handleClose}
        paddingSize="xl"
        onNext={handleOnNext}
        onPrev={handleOnPrev}
        {...(showActions && {
          footer: (
            <Row alignItems="center" justifyContent="space-between">
              <Row.Item shrink>
                <Button
                  kind="negative"
                  onClick={handleReject}
                  label={l10n.getString("approval-action-reject")}
                  testId="reject-approval"
                  disabled={isActionLoading}
                />
              </Row.Item>
              <Row.Item shrink>
                <Button
                  kind="primary"
                  onClick={handleClickApprove}
                  label={l10n.getString("approval-action-approve")}
                  testId="approve-approval"
                  disabled={isActionLoading}
                />
              </Row.Item>
            </Row>
          ),
        })}
      >
        <div ref={drawerContentRef}>
          <Row alignItems="center" className="padding--top--l">
            <Row.Item shrink>
              <h1 className="margin--none">{l10n.getString(titleL10nKey)}</h1>
            </Row.Item>
            <Row.Item>
              <div className={styles.drawerStatusTag}>
                {renderStatusIndicator()}
              </div>
            </Row.Item>
          </Row>
          {!isBulk && !isWireImportBatch && !isBulkReversal && (
            <TopCard
              dualApproval={dualApproval}
              isPendingDrawer={isPendingDrawer}
              originalPayment={originalPayment}
              handleClickOriginalPayment={
                isReversal ? handleClickOriginalPayment : undefined
              }
            />
          )}
          {isBulk && (
            <BulkPaymentTopCard
              dualApproval={dualApproval}
              handleClickViewPayments={toggleBulkPaymentsDialog}
            />
          )}
          {isBulkReversal && (
            <BulkReversalTopCard
              dualApproval={dualApproval}
              handleClickViewPayments={toggleBulkPaymentsDialog}
              handleClickOriginalPayment={handleClickOriginalPayment}
            />
          )}
          {isWireImportBatch && (
            <WireImportTopCard
              dualApproval={dualApproval}
              handleClickViewPayments={toggleWireImportDialog}
            />
          )}
          {deliveryCopy ? (
            <div className="fontSize--xs margin--top--xs">{deliveryCopy}</div>
          ) : null}
          {!isWireImportBatch && (
            <RecipientDetailsCard dualApproval={dualApproval} />
          )}
          <PaymentDetailsCard
            dualApproval={dualApproval}
            originalPayment={originalPayment}
            parentBulkPayment={parentBulkPayment}
          />
        </div>
      </Drawer>
      <ReviewPaymentDialog
        isOpen={isBulkPaymentsDialogOpen}
        handleClose={toggleBulkPaymentsDialog}
        payments={paymentsForDialog}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        paymentsShape="paymentActivity"
      />
      <WireReviewPaymentDialog
        isOpen={isWireImportDialogOpen}
        handleClose={toggleWireImportDialog}
        payments={wireImportPaymentsForDialog}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
      />
      {showActions && isScheduledACH ? (
        <DualApprovalRecurringDialog
          isOpen={visibleDialog === "recurring"}
          handleClose={toggleRecurringDialog}
          handleApprove={handleApprove || (() => {})}
        />
      ) : null}
      {showActions && isBulk ? (
        <BulkApprovalDialog
          isOpen={visibleDialog === "bulkApproval"}
          handleClose={toggleBulkApprovalDialog}
          handleApprove={handleApprove || (() => {})}
        />
      ) : null}
    </>
  );
};

export default DualApprovalDrawer;
